import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct } from "../lib/api";
import { useCart } from "../lib/cart";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { MakeOfferDialog } from "../components/MakeOfferDialog";
import { Star, ShoppingCart, ArrowLeft, Lightning, Package, ShieldCheck, Truck, Handshake } from "@phosphor-icons/react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [offerOpen, setOfferOpen] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    fetchProduct(id)
      .then(setProduct)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
          <div className="aspect-square bg-zinc-100" />
          <div className="space-y-4">
            <div className="h-6 bg-zinc-100 w-1/4" />
            <div className="h-12 bg-zinc-100 w-3/4" />
            <div className="h-32 bg-zinc-100" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="font-mono uppercase tracking-[0.2em] text-zinc-500">Product not found</p>
        <Link to="/products" className="inline-block mt-4 underline font-mono uppercase text-xs">
          Back to catalog
        </Link>
      </div>
    );
  }

  const discount =
    product.original_price && product.original_price > product.price
      ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
      : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="product-detail-page">
      <Link to="/products" className="inline-flex items-center gap-1 font-mono uppercase text-xs tracking-[0.2em] mb-6 hover:text-[#002FA7]">
        <ArrowLeft size={12} weight="bold" /> Back to catalog
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="border-2 border-zinc-950 bg-zinc-50 aspect-square overflow-hidden relative">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          {product.is_deal && (
            <Badge className="absolute top-4 left-4 rounded-none bg-[#FF2A00] hover:bg-[#FF2A00] text-white font-mono uppercase tracking-wider">
              <Lightning size={12} weight="fill" className="mr-1" /> Hot Deal
            </Badge>
          )}
        </div>

        <div className="flex flex-col">
          <div className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500 mb-2">
            {product.category} · {product.brand} · SKU {product.sku || product.id.slice(0, 8)}
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1] mb-4" data-testid="product-name">
            {product.name}
          </h1>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={16} weight={i <= Math.round(product.rating) ? "fill" : "regular"} className="text-amber-500" />
              ))}
            </div>
            <span className="font-mono text-sm">{product.rating}</span>
            <span className="text-sm text-zinc-500">({product.reviews_count} reviews)</span>
          </div>

          <div className="border-y-2 border-zinc-950 py-6 mb-6">
            <div className="flex items-end gap-4">
              <div className="font-mono text-5xl font-bold tracking-tight" data-testid="product-price">
                ${product.price.toFixed(2)}
              </div>
              {product.original_price && product.original_price > product.price && (
                <>
                  <div className="font-mono text-xl text-zinc-400 line-through pb-2">
                    ${product.original_price.toFixed(2)}
                  </div>
                  <div className="bg-[#FF2A00] text-white px-2 py-0.5 font-mono text-sm font-bold mb-2">
                    -{discount}%
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3 font-mono text-xs uppercase tracking-wider">
              {product.stock > 0 ? (
                <>
                  <Package size={14} weight="bold" className="text-green-600" />
                  <span className="text-green-600">In stock — {product.stock} available</span>
                </>
              ) : (
                <span className="text-red-600">Out of stock</span>
              )}
            </div>
          </div>

          <p className="text-base text-zinc-700 leading-relaxed mb-6" data-testid="product-description">
            {product.description}
          </p>

          {Object.keys(product.specs || {}).length > 0 && (
            <div className="border border-zinc-200 mb-6" data-testid="product-specs">
              <div className="bg-zinc-950 text-white px-4 py-2 font-mono uppercase text-xs tracking-[0.2em]">
                Specifications
              </div>
              <div className="divide-y divide-zinc-100">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="flex justify-between px-4 py-2 text-sm">
                    <span className="font-mono uppercase text-xs tracking-wider text-zinc-500">{k}</span>
                    <span className="font-mono text-zinc-950">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 items-stretch mb-4">
            <div className="flex border-2 border-zinc-950">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-10 hover:bg-zinc-100 font-mono"
                data-testid="qty-decrease"
              >
                −
              </button>
              <span className="w-12 text-center self-center font-mono font-bold" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => Math.min(product.stock, q + 1))}
                className="w-10 hover:bg-zinc-100 font-mono"
                data-testid="qty-increase"
              >
                +
              </button>
            </div>
            <Button
              onClick={() => addItem(product, qty)}
              disabled={product.stock === 0}
              data-testid="add-to-cart-detail-button"
              className="flex-1 rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider h-12"
            >
              <ShoppingCart size={18} weight="bold" className="mr-2" />
              Add to cart · ${(product.price * qty).toFixed(2)}
            </Button>
          </div>

          <Button
            onClick={() => setOfferOpen(true)}
            disabled={product.stock === 0}
            data-testid="make-offer-button"
            variant="outline"
            className="w-full rounded-none border-2 border-zinc-950 hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] font-bold uppercase tracking-wider h-12 mb-4"
          >
            <Handshake size={18} weight="bold" className="mr-2" />
            Make an Offer
          </Button>

          <MakeOfferDialog
            product={product}
            open={offerOpen}
            onOpenChange={setOfferOpen}
          />

          <div className="grid grid-cols-3 gap-2 mt-6">
            <div className="border border-zinc-200 p-3 flex items-center gap-2">
              <Truck size={20} weight="bold" />
              <div className="font-mono text-[10px] uppercase tracking-wider">Fast ship</div>
            </div>
            <div className="border border-zinc-200 p-3 flex items-center gap-2">
              <ShieldCheck size={20} weight="bold" />
              <div className="font-mono text-[10px] uppercase tracking-wider">Secure pay</div>
            </div>
            <div className="border border-zinc-200 p-3 flex items-center gap-2">
              <Package size={20} weight="bold" />
              <div className="font-mono text-[10px] uppercase tracking-wider">2yr warranty</div>
            </div>
          </div>

          {product.newegg_url && (
            <a
              href={product.newegg_url}
              target="_blank"
              rel="noreferrer"
              data-testid="newegg-link"
              className="mt-4 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] underline"
            >
              View on Newegg →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
